// Utilities
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";
import type { Portal } from "@/types/portal";
import type { User } from "@/types/user";
import type { BusinessProfile } from "@/types/businessProfile";
import { newSessionName } from "@/helpers/sessionName";
import { getAccessToken } from "@/api/idp";
import AppService from "@/services/appService";

interface AppState {
  portal: Portal | null;
  user: User | null;
  businessProfiles: BusinessProfile[];
  businessProfileUsers: User[];
  selectedBusinessProfileLogo: string;
  selectedBusinessProfileId: number | null;
  sessionId: string;
  sessionName: string;
  isSessionRenamed: boolean;
  isInEmbedMode: boolean;
  TraceSessionId: string;

}
export const useAppStore = defineStore("app", {
  state: (): AppState => ({
    portal: null,
    user: null,
    businessProfiles: [],
    businessProfileUsers: [],
    selectedBusinessProfileId: null,
    selectedBusinessProfileLogo: "",
    sessionId: uuidv4(),
    sessionName: newSessionName(),
    isSessionRenamed: false,
    isInEmbedMode: false,
    TraceSessionId: ''
  }),
  getters: {
    getPortalDomain(): string {
      return window.location.hostname;
    },
    getPortalId(): number | null {
      return this.portal?.id || null
    },
    getTraceSessionId(): string {
      return this.TraceSessionId
    }
  },
  actions: {
    setPortal(portal: Portal | null): void {
      this.portal = portal;
    },
    setUser(user: User | null): void {
      this.user = user;
    },
    setBusinessProfiles(businessProfiles: BusinessProfile[]): void {
      this.businessProfiles = businessProfiles;
    },
    setBusinessProfileUsers(users: User[]): void {
      this.businessProfileUsers = users.sort((a, b) =>
        (a.fullName ?? "").localeCompare(b.fullName ?? "")
      );
    },
    setSelectedBusinessProfileId(
      selectedBusinessProfileId: number | null
    ): void { 
      this.selectedBusinessProfileId = selectedBusinessProfileId;
    },
    setSessionId(sessionId?: string): void {
      if (sessionId) { 
        this.sessionId = sessionId;
        return;
      }
      this.sessionId = uuidv4();
    },
    setSessionName(sessionName?: string | null, isRenamed?: boolean): void {
      const name = sessionName?.trim();
      this.sessionName = name || newSessionName();
      this.isSessionRenamed = isRenamed ?? !!name;
    },
    renameSession(sessionName: string): void {
      this.sessionName = sessionName;
      this.isSessionRenamed = true;
    },
    setIsInEmbedMode(isInEmbedMode: boolean): void {
      this.isInEmbedMode = isInEmbedMode;
    },
    setTraceSessionId(): void {
      this.TraceSessionId = uuidv4()
    },
    async hydrateUserFromToken(): Promise<User | null> {
      if (this.user) {
        return this.user
      }

      if (!getAccessToken()) {
        return null
      }

      const appService = new AppService()
      const user = await appService.getCurrentUser()
      user.isSignedIn = true
      this.setUser(user)
      return user
    },
  },
});
